import { SplitTypes } from 'domains/campaigns/types';

import { CampaignStateTypes } from '../customCampaignReducer';
import { isSplitBranchRootSend } from '../utils';

/**
 * A single send rendered in the navigation, plus whatever follows it in its own branch.
 * `children` are the items chained/nested AFTER this send within the same branch.
 */
export type NavSend = {
    kind: 'send';
    index: number;
    depth: number;
    children: NavItem[];
};

/**
 * A conditional split: its MEETS and DOES_NOT_MEET branch roots. Rendered as a bordered,
 * indented box so nesting is visually obvious. `depth` is the nesting level (0 = top-level).
 */
export type NavSplit = {
    kind: 'split';
    depth: number;
    meets: NavSend;
    doesNotMeet: NavSend;
};

export type NavItem = NavSend | NavSplit;

type Sends = CampaignStateTypes['sends'];

/**
 * Builds a nested render tree from the flat sends array so the non-graph navigation can
 * box-and-indent nested conditional splits. A MEETS branch root paired with its DOES_NOT_MEET
 * sibling becomes a `split` node; everything else is a `send` node. Pairing uses splitNodeId
 * (so nested splits, whose splitType repeats their parent branch's, are matched correctly) and
 * falls back to parentIndex for the legacy top-level split that carries no splitNodeId.
 */
export const buildNavForest = (sends: Sends): NavItem[] => {
    const childIndices = (parentIndex: number | null): number[] =>
        sends.reduce<number[]>((acc, send, i) => {
            const p = (send as any).parentIndex ?? null;
            if (p === parentIndex) acc.push(i);
            return acc;
        }, []);

    const findDoesNotMeetSibling = (meetsIndex: number): number => {
        const meets = sends[meetsIndex] as any;
        return sends.findIndex(
            (s: any, i) =>
                i !== meetsIndex &&
                s.splitType === SplitTypes.DOES_NOT_MEET &&
                isSplitBranchRootSend(s, sends) &&
                (meets.splitNodeId != null
                    ? s.splitNodeId === meets.splitNodeId
                    : s.splitNodeId == null && (s.parentIndex ?? null) === (meets.parentIndex ?? null))
        );
    };

    const buildChildren = (parentIndex: number | null, depth: number): NavItem[] => {
        const consumed = new Set<number>();
        const items: NavItem[] = [];

        for (const idx of childIndices(parentIndex)) {
            if (consumed.has(idx)) continue;
            const send = sends[idx] as any;
            const opensSplit = isSplitBranchRootSend(send, sends) && send.splitType === SplitTypes.MEETS;

            if (opensSplit) {
                const dnmIndex = findDoesNotMeetSibling(idx);
                if (dnmIndex !== -1) {
                    consumed.add(idx);
                    consumed.add(dnmIndex);
                    items.push({
                        kind: 'split',
                        depth,
                        meets: { kind: 'send', index: idx, depth, children: buildChildren(idx, depth + 1) },
                        doesNotMeet: {
                            kind: 'send',
                            index: dnmIndex,
                            depth,
                            children: buildChildren(dnmIndex, depth + 1),
                        },
                    });
                    continue;
                }
            }

            // Regular chained send — stays at the same depth (chains are not indented).
            items.push({ kind: 'send', index: idx, depth, children: buildChildren(idx, depth) });
        }

        return items;
    };

    return buildChildren(null, 0);
};
